import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  type HugeNumber,
} from "@/shared/numbers";

export type HovSkillId =
  | "amplifier-unlock"
  | "hg-exponent-bonus"
  | "hg-hng-simultaneous-purchase-count-base-bonus"
  | "mp-capacity-bonus"
  | "hov-1-gain-multiplier-bonus"
  | "amplifier-gain-ohov-multiplier-bonus"
  | "ohov-hov-core-gain-multiplier-bonus"
  | "mhov-mp-control-unlock"
  | "amplifier-panel-mp-placement-unlock"
  | "hng-rank-scale-base-bonus"
  | "wall-unlock"
  | "ra-plum-amplifier-gain-multiplier-bonus"
  | "ra-bamboo-amplifier-gain-multiplier-bonus"
  | "ra-pine-amplifier-gain-multiplier-bonus"
  | "ap-boar-exponent-bonus"
  | "ap-deer-exponent-bonus"
  | "ap-butterfly-exponent-bonus"
  | "nhov-mp-control-unlock"
  | "variety-pack"
  | "mp-capacity-second-bonus"
  | "mp-capacity-core"
  | "free-mp-core"
  | "wall-cap-relaxation"
  | "exploration-unlock";

export type HovSkillCostResource = "hovCore";

export interface HovSkillCost {
  readonly resource: HovSkillCostResource;
  readonly amount: HugeNumber;
}

export interface HovSkillResourceBalances {
  readonly hovCore: HugeNumber;
}

export interface HovSkillCoordinate {
  readonly row: number;
  readonly column: number;
}

export interface HovSkillDefinition extends HovSkillCoordinate {
  readonly id: HovSkillId;
  readonly cost: HovSkillCost;
  readonly prerequisites: readonly HovSkillId[];
}

export const HOV_SKILL_HOLD_MS_OPTIONS = [0, 1000, 3000] as const;

export type HovSkillHoldMs = (typeof HOV_SKILL_HOLD_MS_OPTIONS)[number];

export const HOV_SKILL_DEFINITIONS: readonly HovSkillDefinition[] = [
  {
    id: "amplifier-unlock",
    cost: { resource: "hovCore", amount: hugeOne() },
    prerequisites: [],
    row: 0,
    column: 0,
  },
  {
    id: "hg-exponent-bonus",
    cost: { resource: "hovCore", amount: hugeOne() },
    prerequisites: ["amplifier-unlock"],
    row: 1,
    column: 0,
  },
  {
    id: "hg-hng-simultaneous-purchase-count-base-bonus",
    cost: { resource: "hovCore", amount: huge(2) },
    prerequisites: ["amplifier-unlock"],
    row: 1,
    column: 1,
  },
  {
    id: "mp-capacity-bonus",
    cost: { resource: "hovCore", amount: hugeOne() },
    prerequisites: ["amplifier-unlock"],
    row: 1,
    column: 2,
  },
  {
    id: "hov-1-gain-multiplier-bonus",
    cost: { resource: "hovCore", amount: hugeOne() },
    prerequisites: ["hg-exponent-bonus", "mp-capacity-bonus"],
    row: 2,
    column: 0,
  },
  {
    id: "amplifier-gain-ohov-multiplier-bonus",
    cost: { resource: "hovCore", amount: huge(2) },
    prerequisites: ["hov-1-gain-multiplier-bonus"],
    row: 3,
    column: 0,
  },
  {
    id: "ohov-hov-core-gain-multiplier-bonus",
    cost: { resource: "hovCore", amount: huge(2) },
    prerequisites: ["amplifier-gain-ohov-multiplier-bonus"],
    row: 4,
    column: 0,
  },
  {
    id: "mhov-mp-control-unlock",
    cost: { resource: "hovCore", amount: huge(10) },
    prerequisites: ["amplifier-gain-ohov-multiplier-bonus"],
    row: 4,
    column: 1,
  },
  {
    id: "amplifier-panel-mp-placement-unlock",
    cost: { resource: "hovCore", amount: huge(2) },
    prerequisites: ["amplifier-gain-ohov-multiplier-bonus"],
    row: 4,
    column: 2,
  },
  {
    id: "hng-rank-scale-base-bonus",
    cost: { resource: "hovCore", amount: huge(5) },
    prerequisites: [
      "ohov-hov-core-gain-multiplier-bonus",
      "amplifier-panel-mp-placement-unlock",
    ],
    row: 5,
    column: 0,
  },
  {
    id: "wall-unlock",
    cost: { resource: "hovCore", amount: huge(10) },
    prerequisites: ["hng-rank-scale-base-bonus"],
    row: 6,
    column: 0,
  },
  {
    id: "ra-plum-amplifier-gain-multiplier-bonus",
    cost: { resource: "hovCore", amount: huge(15) },
    prerequisites: ["wall-unlock"],
    row: 7,
    column: 0,
  },
  {
    id: "ra-bamboo-amplifier-gain-multiplier-bonus",
    cost: { resource: "hovCore", amount: huge(15) },
    prerequisites: ["wall-unlock"],
    row: 7,
    column: 1,
  },
  {
    id: "ra-pine-amplifier-gain-multiplier-bonus",
    cost: { resource: "hovCore", amount: huge(15) },
    prerequisites: ["wall-unlock"],
    row: 7,
    column: 2,
  },
  {
    id: "ap-boar-exponent-bonus",
    cost: { resource: "hovCore", amount: huge(50) },
    prerequisites: ["ra-plum-amplifier-gain-multiplier-bonus"],
    row: 8,
    column: 0,
  },
  {
    id: "ap-deer-exponent-bonus",
    cost: { resource: "hovCore", amount: huge(50) },
    prerequisites: ["ra-bamboo-amplifier-gain-multiplier-bonus"],
    row: 8,
    column: 1,
  },
  {
    id: "ap-butterfly-exponent-bonus",
    cost: { resource: "hovCore", amount: huge(50) },
    prerequisites: ["ra-pine-amplifier-gain-multiplier-bonus"],
    row: 8,
    column: 2,
  },
  {
    id: "nhov-mp-control-unlock",
    cost: { resource: "hovCore", amount: huge(100) },
    prerequisites: ["ap-boar-exponent-bonus", "ap-deer-exponent-bonus"],
    row: 9,
    column: 0,
  },
  {
    id: "variety-pack",
    cost: { resource: "hovCore", amount: huge(300) },
    prerequisites: ["ap-deer-exponent-bonus", "ap-butterfly-exponent-bonus"],
    row: 9,
    column: 1,
  },
  {
    id: "mp-capacity-second-bonus",
    cost: { resource: "hovCore", amount: huge(1000) },
    prerequisites: ["nhov-mp-control-unlock"],
    row: 10,
    column: 0,
  },
  {
    id: "mp-capacity-core",
    cost: { resource: "hovCore", amount: huge(2000) },
    prerequisites: ["nhov-mp-control-unlock", "variety-pack"],
    row: 10,
    column: 1,
  },
  {
    id: "free-mp-core",
    cost: { resource: "hovCore", amount: huge(1000) },
    prerequisites: ["variety-pack"],
    row: 10,
    column: 2,
  },
  {
    id: "wall-cap-relaxation",
    cost: { resource: "hovCore", amount: huge(1e6) },
    prerequisites: ["mp-capacity-second-bonus", "free-mp-core"],
    row: 11,
    column: 1,
  },
  {
    id: "exploration-unlock",
    cost: { resource: "hovCore", amount: huge(1e10) },
    prerequisites: ["wall-cap-relaxation"],
    row: 12,
    column: 1,
  },
];

export const HOV_SKILL_HOLD_MS: HovSkillHoldMs = 3000;

const HOV_SKILL_IDS = new Set<HovSkillId>(
  HOV_SKILL_DEFINITIONS.map((definition) => definition.id),
);

export function isHovSkillId(value: string): value is HovSkillId {
  return HOV_SKILL_IDS.has(value as HovSkillId);
}

export function isHovSkillHoldMs(value: number): value is HovSkillHoldMs {
  return HOV_SKILL_HOLD_MS_OPTIONS.includes(value as HovSkillHoldMs);
}

export function findHovSkillDefinitionById(
  id: string,
): HovSkillDefinition | undefined {
  return HOV_SKILL_DEFINITIONS.find((definition) => definition.id === id);
}

export function getHovSkillDefinition(id: HovSkillId): HovSkillDefinition {
  const def = findHovSkillDefinitionById(id);
  if (!def) {
    throw new Error(`Unknown hov skill id: ${id}`);
  }
  return def;
}

export function getHovSkillCostBalance(
  cost: HovSkillCost,
  resources: HovSkillResourceBalances,
): HugeNumber {
  switch (cost.resource) {
    case "hovCore":
      return resources.hovCore;
  }
}

export function canAffordHovSkillCost(
  cost: HovSkillCost,
  resources: HovSkillResourceBalances,
): boolean {
  return compareHuge(getHovSkillCostBalance(cost, resources), cost.amount) >= 0;
}

export function getHovSkillCostShortfall(
  cost: HovSkillCost,
  resources: HovSkillResourceBalances,
): HugeNumber {
  const missing = cost.amount
    .sub(getHovSkillCostBalance(cost, resources))
    .ceil();

  return compareHuge(missing, 0) > 0 ? missing : hugeZero();
}
